import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, RefreshCw } from "lucide-react";
import { useLang } from "@/i18n";
import { adminGetAppointments, adminCreateAppointment, adminUpdateAppointment, adminDeleteAppointment } from "@/lib/api";
import { cx } from "@/ui";

const CITIES = ["Toluca", "CDMX", "Querétaro", "Monterrey", "Cancún"];
const DAY_START = 7;   // 07:00
const DAY_END = 21;    // 21:00
const HOUR_PX = 52;
const STATUSES = ["pending", "confirmed", "blocked", "cancelled"];

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const toTime = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

function mondayOf(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

const STATUS_STYLE = {
  pending: { bg: "rgba(199,166,104,.16)", border: "#C7A668", text: "#E4Cd9a" },
  confirmed: { bg: "rgba(52,211,153,.15)", border: "#34d399", text: "#6ee7b7" },
  blocked: { bg: "rgba(120,120,128,.18)", border: "#8a8f98", text: "#c9ced4" },
  cancelled: { bg: "rgba(255,90,90,.12)", border: "#ef4444", text: "#fca5a5" },
};

export default function AgendaCalendar({ token }) {
  const { L } = useLang();
  const [city, setCity] = useState(CITIES[0]);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [appts, setAppts] = useState([]);
  const [editing, setEditing] = useState(null); // appt object or {__new:true,...}
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const hours = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
  const weekDates = days.map(fmtDate);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try { const data = await adminGetAppointments(token, city); setAppts(data); }
    catch (e) { console.error("Load appointments failed", e); }
    if (!silent) setLoading(false);
  }, [token, city]);

  useEffect(() => { load(); }, [load]);

  // Real-time-ish: poll every 10s
  useEffect(() => {
    pollRef.current = setInterval(() => load(true), 10000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const weekAppts = appts.filter((a) => weekDates.includes(a.date));

  const openNew = (date) => setEditing({ __new: true, city, date, startTime: "09:00", endTime: "12:00", type: "service", status: "confirmed", clientName: "", aircraftModel: "", serviceType: "", notes: "" });

  const save = async (data) => {
    const body = { city: data.city, date: data.date, startTime: data.startTime, endTime: data.endTime, type: data.type, status: data.status, clientName: data.clientName, aircraftModel: data.aircraftModel, serviceType: data.serviceType, notes: data.notes };
    if (data.__new) await adminCreateAppointment(token, body);
    else await adminUpdateAppointment(token, data.apptId, body);
    setEditing(null);
    load(true);
  };
  const remove = async (id) => { await adminDeleteAppointment(token, id); setEditing(null); load(true); };

  const shiftWeek = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d); };
  const ml = weekStart.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const monthLabel = ml.charAt(0).toUpperCase() + ml.slice(1);

  return (
    <div className="mt-8" data-testid="agenda-panel">
      {/* City sub-tabs (sedes) */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-white/10 pb-3">
        {CITIES.map((c) => (
          <button key={c} data-testid={`agenda-city-${c}`} onClick={() => setCity(c)}
            className={cx("font-mono text-[11px] uppercase tracking-widest px-3.5 py-2 rounded-sm border transition-all", city === c ? "border-[#C7A668] text-[#C7A668] bg-[#C7A668]/10" : "border-white/12 text-white/45 hover:text-white/80")}>
            {c}
          </button>
        ))}
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between mt-5 mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="agenda-prev-week" onClick={() => shiftWeek(-1)} className="p-2 rounded-sm border border-white/12 text-white/60 hover:text-white"><ChevronLeft size={16} /></button>
          <button data-testid="agenda-today" onClick={() => setWeekStart(mondayOf(new Date()))} className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-white/12 text-white/60 hover:text-white">{L("Today", "Hoy")}</button>
          <button data-testid="agenda-next-week" onClick={() => shiftWeek(1)} className="p-2 rounded-sm border border-white/12 text-white/60 hover:text-white"><ChevronRight size={16} /></button>
          <span className="font-display text-lg text-white/85">{monthLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="agenda-refresh" onClick={() => load()} className="p-2 rounded-sm border border-white/12 text-white/60 hover:text-white"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          <button data-testid="agenda-new" onClick={() => openNew(fmtDate(days[0]))} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-3.5 py-2 rounded-sm border border-[#C7A668] text-[#C7A668] hover:bg-[#C7A668] hover:text-black transition-all"><Plus size={14} />{L("Block / Add", "Bloquear / Agregar")}</button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="border border-white/10 rounded-sm overflow-x-auto bg-white/[0.015]">
        <div style={{ minWidth: 820 }}>
          {/* header row */}
          <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
            <div className="border-b border-r border-white/10" />
            {days.map((d, i) => {
              const isToday = fmtDate(d) === fmtDate(new Date());
              return (
                <div key={i} className="border-b border-r border-white/10 py-2 text-center">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{d.toLocaleDateString("es-MX", { weekday: "short" })}</div>
                  <div className={cx("text-sm mt-0.5", isToday ? "text-[#C7A668] font-semibold" : "text-white/70")}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>
          {/* body */}
          <div className="grid relative" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
            {/* hour labels */}
            <div className="border-r border-white/10">
              {hours.map((h) => (
                <div key={h} style={{ height: HOUR_PX }} className="border-b border-white/10 pr-1.5 text-right">
                  <span className="font-mono text-[10px] text-white/30 relative -top-1.5">{pad(h)}:00</span>
                </div>
              ))}
            </div>
            {/* day columns */}
            {days.map((d, di) => {
              const dateStr = fmtDate(d);
              const dayAppts = weekAppts.filter((a) => a.date === dateStr);
              return (
                <div key={di} className="border-r border-white/10 relative" data-testid={`agenda-col-${di}`}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const mins = DAY_START * 60 + Math.floor(y / HOUR_PX) * 60;
                    setEditing({ __new: true, city, date: dateStr, startTime: toTime(Math.max(DAY_START * 60, mins)), endTime: toTime(Math.min(DAY_END * 60, mins + 180)), type: "service", status: "confirmed", clientName: "", aircraftModel: "", serviceType: "", notes: "" });
                  }}>
                  {hours.map((h) => <div key={h} style={{ height: HOUR_PX }} className="border-b border-white/10" />)}
                  {dayAppts.map((a) => {
                    const s = Math.max(toMin(a.startTime), DAY_START * 60);
                    const e2 = Math.min(toMin(a.endTime), DAY_END * 60);
                    const top = ((s - DAY_START * 60) / 60) * HOUR_PX;
                    const height = Math.max(22, ((e2 - s) / 60) * HOUR_PX - 2);
                    const st = STATUS_STYLE[a.status] || STATUS_STYLE.confirmed;
                    return (
                      <div key={a.apptId} data-testid={`appt-${a.apptId}`}
                        onClick={(ev) => { ev.stopPropagation(); setEditing({ ...a }); }}
                        className="absolute left-0.5 right-0.5 rounded-sm px-1.5 py-1 cursor-pointer overflow-hidden"
                        style={{ top, height, background: st.bg, borderLeft: `3px solid ${st.border}` }}>
                        <div className="font-mono text-[9px] tracking-wide" style={{ color: st.text }}>{a.startTime}–{a.endTime}</div>
                        <div className="text-[11px] leading-tight text-white/90 truncate">{a.type === "block" ? (a.notes || L("Blocked", "Bloqueado")) : (a.clientName || a.aircraftModel || a.serviceType || L("Service", "Servicio"))}</div>
                        {a.type !== "block" && a.aircraftModel && <div className="text-[10px] text-white/45 truncate">{a.aircraftModel}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-white/35 text-xs mt-3 font-light">{L("Click an empty cell to block/add. Click a service to edit, move or extend it. Updates auto-refresh.", "Toca una celda vacía para bloquear/agregar. Toca un servicio para editar, mover o extender. Se actualiza solo.")}</p>

      {editing && <ApptModal data={editing} onClose={() => setEditing(null)} onSave={save} onDelete={remove} />}
    </div>
  );
}

function ApptModal({ data, onClose, onSave, onDelete }) {
  const { L } = useLang();
  const [f, setF] = useState(data);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const extend = (mins) => set("endTime", toTime(Math.min(DAY_END * 60, toMin(f.endTime) + mins)));
  const invalidTime = toMin(f.endTime) <= toMin(f.startTime);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div data-testid="appt-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0c0d0e] border border-white/12 rounded-sm p-6" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-white">{f.__new ? L("New entry", "Nueva entrada") : (f.linkedRequestId || L("Edit entry", "Editar entrada"))}</h3>
          <button data-testid="appt-close" onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Type", "Tipo")}</span>
              <select data-testid="appt-type" value={f.type} onChange={(e) => set("type", e.target.value)} className="input-lux text-sm py-2 mt-1">
                <option value="service">{L("Service", "Servicio")}</option>
                <option value="block">{L("Block", "Bloqueo")}</option>
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Status", "Estatus")}</span>
              <select data-testid="appt-status" value={f.status} onChange={(e) => set("status", e.target.value)} className="input-lux text-sm py-2 mt-1">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Date", "Fecha")}</span>
            <input data-testid="appt-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="input-lux text-sm py-2 mt-1" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Start", "Inicio")}</span>
              <input data-testid="appt-start" type="time" value={f.startTime} onChange={(e) => set("startTime", e.target.value)} className="input-lux text-sm py-2 mt-1" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("End", "Fin")}</span>
              <input data-testid="appt-end" type="time" value={f.endTime} onChange={(e) => set("endTime", e.target.value)} className="input-lux text-sm py-2 mt-1" />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/35">{L("Extend", "Extender")}</span>
            <button data-testid="appt-extend-30" onClick={() => extend(30)} className="font-mono text-[11px] px-2.5 py-1 rounded-sm border border-white/15 text-white/60 hover:text-white">+30m</button>
            <button data-testid="appt-extend-60" onClick={() => extend(60)} className="font-mono text-[11px] px-2.5 py-1 rounded-sm border border-white/15 text-white/60 hover:text-white">+1h</button>
          </div>

          {f.type !== "block" && (
            <>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Client", "Cliente")}</span>
                <input data-testid="appt-client" value={f.clientName || ""} onChange={(e) => set("clientName", e.target.value)} className="input-lux text-sm py-2 mt-1" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Aircraft", "Aeronave")}</span>
                <input data-testid="appt-aircraft" value={f.aircraftModel || ""} onChange={(e) => set("aircraftModel", e.target.value)} className="input-lux text-sm py-2 mt-1" />
              </label>
            </>
          )}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{L("Notes", "Notas")}</span>
            <input data-testid="appt-notes" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="input-lux text-sm py-2 mt-1" />
          </label>
        </div>

        <div className="flex items-center justify-between mt-6 gap-3">
          {!f.__new
            ? <button data-testid="appt-delete" onClick={() => onDelete(f.apptId)} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-red-400/80 hover:text-red-400"><Trash2 size={14} />{L("Delete / Free", "Eliminar / Liberar")}</button>
            : <span />}
          <button data-testid="appt-save" disabled={invalidTime} onClick={() => onSave(f)} className={cx("font-semibold px-6 py-2.5 rounded-sm text-sm transition-all", invalidTime ? "bg-white/15 text-white/40 cursor-not-allowed" : "bg-[#F2EFE8] text-black hover:opacity-90")}>{invalidTime ? L("End must be after start", "Fin después de inicio") : L("Save", "Guardar")}</button>
        </div>
      </div>
    </div>
  );
}
